const SELECTOR = '[data-fbwa-on-visible="true"].fbwa-on-visible';
const DISMISS_SELECTOR = '[data-fbwa-dismissible="true"]';
const DISMISS_STORAGE_PREFIX = 'fbwa-dismiss:';
const AB_SELECTOR = '[data-fbwa-ab-test="true"]';
const AB_STORAGE_PREFIX = 'fbwa-ab:';

function revealImmediately(blocks) {
	blocks.forEach((block) => block.classList.add('is-visible'));
}

function initOnVisibleAnimations() {
	document.documentElement.classList.add('fbwa-anim-ready');

	const blocks = Array.from(document.querySelectorAll(SELECTOR));
	if (!blocks.length) {
		return;
	}

	const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
	if (prefersReducedMotion || !('IntersectionObserver' in window)) {
		revealImmediately(blocks);
		return;
	}

	const observer = new IntersectionObserver(
		(entries) => {
			entries.forEach((entry) => {
				if (!entry.isIntersecting) {
					return;
				}
				entry.target.classList.add('is-visible');
				observer.unobserve(entry.target);
			});
		},
		{
			threshold: 0.2,
			rootMargin: '0px 0px -5% 0px',
		}
	);

	blocks.forEach((block) => observer.observe(block));
}

function getDismissStorageKey(block) {
	const configuredKey = (block.getAttribute('data-fbwa-dismiss-key') || '').trim();
	if (configuredKey) {
		return `${DISMISS_STORAGE_PREFIX}${configuredKey}`;
	}

	// Fallback intentionally broad: identical wrappers share the same dismiss state.
	const fallbackSource = (block.getAttribute('id') || block.className || 'fbwa').trim();
	return `${DISMISS_STORAGE_PREFIX}auto:${fallbackSource.replace(/\s+/g, '.').toLowerCase()}`;
}

function isDismissed(storageKey) {
	try {
		const raw = window.localStorage.getItem(storageKey);
		if (!raw) {
			return false;
		}
		const parsed = JSON.parse(raw);
		const expiresAt = Number(parsed?.expiresAt || 0);
		if (!expiresAt || Number.isNaN(expiresAt)) {
			window.localStorage.removeItem(storageKey);
			return false;
		}
		if (Date.now() > expiresAt) {
			window.localStorage.removeItem(storageKey);
			return false;
		}
		return true;
	} catch (error) {
		return false;
	}
}

function storeDismiss(storageKey, days) {
	const safeDays = Math.min(365, Math.max(1, Number(days) || 30));
	const expiresAt = Date.now() + safeDays * 24 * 60 * 60 * 1000;
	try {
		window.localStorage.setItem(storageKey, JSON.stringify({ expiresAt }));
	} catch (error) {
		// If storage fails, we still hide the current instance in this session render.
	}
}

function initDismissibleWrappers() {
	const blocks = Array.from(document.querySelectorAll(DISMISS_SELECTOR));
	if (!blocks.length) {
		return;
	}

	blocks.forEach((block) => {
		const storageKey = getDismissStorageKey(block);
		const dismissDays = block.getAttribute('data-fbwa-dismiss-days') || '30';
		const closeButton = block.querySelector('[data-fbwa-dismiss-trigger="true"]');
		if (!closeButton) {
			block.classList.add('fbwa-dismiss-init');
			return;
		}

		if (isDismissed(storageKey)) {
			block.classList.add('fbwa-dismissed');
			block.classList.add('fbwa-dismiss-init');
			return;
		}
		block.classList.add('fbwa-dismiss-init');

		closeButton.addEventListener('click', () => {
			storeDismiss(storageKey, dismissDays);
			block.classList.add('fbwa-dismissed');
		});
	});
}

function normalizeVariant(value) {
	const variant = String(value || '').trim().toUpperCase();
	return ['A', 'B', 'C'].includes(variant) ? variant : '';
}

function chooseWeightedVariant(weights) {
	const variants = Object.keys(weights);
	if (!variants.length) {
		return 'A';
	}
	const total = variants.reduce((sum, key) => sum + Number(weights[key] || 0), 0);
	if (!total) {
		return variants[0];
	}

	let random = Math.random() * total;
	for (let i = 0; i < variants.length; i += 1) {
		const key = variants[i];
		random -= Number(weights[key] || 0);
		if (random <= 0) {
			return key;
		}
	}
	return variants[variants.length - 1];
}

function getAbStorageKey(experimentKey) {
	return `${AB_STORAGE_PREFIX}${experimentKey}`;
}

function getExperimentKey(block) {
	return String(block.getAttribute('data-fbwa-ab-key') || '').trim();
}

function getVariantWeight(block) {
	const value = Number(block.getAttribute('data-fbwa-ab-weight'));
	if (Number.isNaN(value)) {
		return 100;
	}
	return Math.max(0, value);
}

function collectWeightsByVariant(blocks) {
	const result = {};
	blocks.forEach((block) => {
		const variant = normalizeVariant(block.getAttribute('data-fbwa-ab-variant'));
		if (!variant) {
			return;
		}
		const weight = getVariantWeight(block);
		result[variant] = Math.max(Number(result[variant] || 0), weight);
	});

	const hasPositiveWeight = Object.values(result).some((value) => Number(value) > 0);
	if (!hasPositiveWeight) {
		Object.keys(result).forEach((variant) => {
			result[variant] = 1;
		});
	}
	return result;
}

function getStoredAssignedVariant(experimentKey) {
	const storageKey = getAbStorageKey(experimentKey);
	try {
		const raw = window.localStorage.getItem(storageKey);
		if (!raw) {
			return '';
		}
		const parsed = JSON.parse(raw);
		return normalizeVariant(parsed?.variant);
	} catch (error) {
		return '';
	}
}

function storeAssignedVariant(experimentKey, variant) {
	const storageKey = getAbStorageKey(experimentKey);
	try {
		window.localStorage.setItem(storageKey, JSON.stringify({ variant }));
	} catch (error) {
		// ignore storage errors
	}
}

function dispatchAbEvent(name, detail) {
	try {
		document.dispatchEvent(
			new CustomEvent(name, {
				detail,
			})
		);
	} catch (error) {
		// ignore event errors
	}
}

function resolveAssignedVariant(experimentKey, availableWeights) {
	const params = new URLSearchParams(window.location.search || '');
	const queryVariant = normalizeVariant(params.get(experimentKey));
	if (queryVariant && Object.prototype.hasOwnProperty.call(availableWeights, queryVariant)) {
		storeAssignedVariant(experimentKey, queryVariant);
		return { variant: queryVariant, source: 'query' };
	}

	const storedVariant = getStoredAssignedVariant(experimentKey);
	if (storedVariant && Object.prototype.hasOwnProperty.call(availableWeights, storedVariant)) {
		return { variant: storedVariant, source: 'cookie' };
	}

	const chosen = chooseWeightedVariant(availableWeights);
	storeAssignedVariant(experimentKey, chosen);
	return { variant: chosen, source: 'weighted' };
}

function initAbTestingWrappers() {
	const blocks = Array.from(document.querySelectorAll(AB_SELECTOR));
	if (!blocks.length) {
		return;
	}

	const groups = {};
	blocks.forEach((block) => {
		const experimentKey = getExperimentKey(block);
		if (!experimentKey) {
			block.classList.add('fbwa-ab-init');
			return;
		}
		if (!groups[experimentKey]) {
			groups[experimentKey] = [];
		}
		groups[experimentKey].push(block);
	});

	Object.keys(groups).forEach((experimentKey) => {
		const groupBlocks = groups[experimentKey];
		const availableWeights = collectWeightsByVariant(groupBlocks);
		const assigned = resolveAssignedVariant(experimentKey, availableWeights);
		const assignedVariant = assigned.variant;
		dispatchAbEvent('fbwa:ab-assigned', {
			experimentKey,
			variant: assignedVariant,
			source: assigned.source,
			weights: availableWeights,
		});
		groupBlocks.forEach((block) => {
			const blockVariant = normalizeVariant(block.getAttribute('data-fbwa-ab-variant')) || 'A';
			if (assignedVariant !== blockVariant) {
				block.classList.add('fbwa-ab-hidden');
			}
			if (assignedVariant === blockVariant) {
				dispatchAbEvent('fbwa:ab-block-visible', {
					experimentKey,
					variant: blockVariant,
					block,
				});
			}
			block.classList.add('fbwa-ab-init');
		});
	});

	blocks.forEach((block) => {
		if (block.classList.contains('fbwa-ab-init')) {
			return;
		}
		const blockVariant = normalizeVariant(block.getAttribute('data-fbwa-ab-variant')) || 'A';
		if (!blockVariant) {
			block.classList.add('fbwa-ab-hidden');
		}
		block.classList.add('fbwa-ab-init');
	});
}

if (document.readyState === 'loading') {
	document.addEventListener(
		'DOMContentLoaded',
		() => {
			initOnVisibleAnimations();
			initDismissibleWrappers();
			initAbTestingWrappers();
		},
		{ once: true }
	);
} else {
	initOnVisibleAnimations();
	initDismissibleWrappers();
	initAbTestingWrappers();
}
